import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    Button, 
    Snackbar, 
    Alert, 
    useTheme,
    Card,
    CardContent,
    Avatar,
    LinearProgress,
    Chip,
    Divider,
    Stack,
    IconButton,
    Badge
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    People as PeopleIcon,
    CardMembership as CardMembershipIcon,
    AttachMoney as AttachMoneyIcon,
    Add as AddIcon,
    Dashboard as DashboardIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { usePage } from '../../context/PageContext';
import CreateCardDialog from './CreateCardDialog';
import { getUsers, toggleUserActive, toggleCardEnabled } from '../../api/adminApi';
import UsersTable from './UsersTable';

// Premium styled components with dark theme support
const GlassCard = styled(motion.div)(({ theme }) => ({
    background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.8) 0%, rgba(40, 40, 40, 0.6) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '20px',
    padding: theme.spacing(3),
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
        : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 16px 48px 0 rgba(0, 0, 0, 0.4)'
            : '0 16px 48px 0 rgba(31, 38, 135, 0.25)',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(40, 40, 40, 0.9) 0%, rgba(50, 50, 50, 0.7) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
    }
}));

const PremiumStatCard = styled(motion.div)(({ theme, gradient }) => ({
    background: gradient,
    borderRadius: '20px',
    padding: theme.spacing(3),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 30px 60px rgba(0, 0, 0, 0.2)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
    }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '24px',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    }
}));

const StatCard = ({ title, value, icon, gradient, subtitle, progress }) => (
    <PremiumStatCard
        gradient={gradient}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        whileHover={{ y: -12 }}
    >
        <Stack spacing={2} height="100%">
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Avatar 
                    sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        width: 40, 
                        height: 40,
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                </Avatar>
            </Box>
            
            <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                <Typography 
                    variant="h3" 
                    sx={{ 
                        fontWeight: 'bold', 
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        lineHeight: 1
                    }}
                >
                    {value}
                </Typography>
            </Box>

            {progress !== undefined && (
                <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Progress
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {progress}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={progress}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                bgcolor: 'rgba(255, 255, 255, 0.8)'
                            }
                        }}
                    />
                </Box>
            )}
        </Stack>
    </PremiumStatCard>
);

const AdminDashboard = () => {
    const { setPageTitle } = usePage();
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [usersWithCards, setUsersWithCards] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setPageTitle('Admin Dashboard');
        fetchUsersAndCards();
    }, [setPageTitle]);

    const fetchUsersAndCards = async () => {
        try {
            setLoading(true);
            const users = await getUsers();
            setUsersWithCards(users);
        } catch (error) {
            console.error("Failed to fetch users and cards:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCardCreated = () => {
        setSnackbarOpen(true);
        fetchUsersAndCards();
    };

    const handleToggleUserActive = async (userId) => {
        try {
            await toggleUserActive(userId);
            fetchUsersAndCards();
        } catch (error) {
            console.error("Failed to toggle user active status:", error);
        }
    };

    const handleToggleCardEnabled = async (cardId) => {
        try {
            await toggleCardEnabled(cardId);
            fetchUsersAndCards();
        } catch (error) {
            console.error("Failed to toggle card enabled status:", error);
        }
    };

    const totalUsers = usersWithCards.length;
    const totalCards = usersWithCards.reduce((acc, user) => acc + user.cards.length, 0);
    const activeUsers = usersWithCards.filter(user => user.is_active).length;
    const publishedCards = usersWithCards.reduce((acc, user) => 
        acc + user.cards.filter(card => card.is_published && card.is_enabled).length, 0
    );

    const stats = [
        { 
            title: 'Total Users', 
            value: totalUsers,
            subtitle: `${activeUsers} active users`,
            icon: <PeopleIcon />, 
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            progress: totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0
        },
        { 
            title: 'Portfolio Cards', 
            value: totalCards,
            subtitle: `${publishedCards} published`,
            icon: <CardMembershipIcon />, 
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            progress: totalCards > 0 ? Math.round((publishedCards / totalCards) * 100) : 0
        },
        { 
            title: 'Monthly Revenue', 
            value: '$12,500',
            subtitle: '+23% from last month',
            icon: <AttachMoneyIcon />, 
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            progress: 78
        },
    ];

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
            {/* Premium Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <HeaderSection>
                    <Box position="relative" zIndex={1}>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            justifyContent="space-between" 
                            alignItems={{ xs: 'flex-start', sm: 'center' }} 
                            mb={2}
                            spacing={{ xs: 2, sm: 0 }}
                        >
                            <Box>
                                <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ 
                                    fontSize: { xs: '1.4rem', sm: '2.4rem' }
                                }}>
                                    Welcome back, Admin
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1.1rem' }
                                }}>
                                    Here's what's happening with your platform today
                                </Typography>
                            </Box>
                            <Badge badgeContent={3} color="error">
                                <IconButton 
                                    sx={{ 
                                        bgcolor: 'rgba(255, 255, 255, 0.1)', 
                                        backdropFilter: 'blur(10px)',
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                                    }}
                                >
                                    <AnalyticsIcon sx={{ color: 'white' }} />
                                </IconButton>
                            </Badge>
                        </Stack>
                        
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setDialogOpen(true)}
                            sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                                }
                            }}
                        >
                            Create Card
                        </Button>
                    </Box>
                </HeaderSection>
            </motion.div>

            {/* Main Stats Grid */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                <Grid container spacing={{ xs: 2, sm: 4 }} mb={6}>
                    {stats.map((stat, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={index}>
                            <StatCard {...stat} />
                        </Grid>
                    ))}
                </Grid>
            </motion.div>

            {/* Users Table Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
            >
                <GlassCard style={{ padding: 0, borderRadius: '24px', overflow: 'hidden' }}>
                    <Box p={3} pb={0}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar sx={{ 
                                bgcolor: 'primary.main',
                                boxShadow: theme.palette.mode === 'dark' 
                                    ? '0 4px 12px rgba(255, 255, 255, 0.1)'
                                    : '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }}>
                                <DashboardIcon />
                            </Avatar>
                            <Box>
                                <Typography 
                                    variant="h6" 
                                    fontWeight="bold" 
                                    color={theme.palette.mode === 'dark' ? 'white' : 'primary.main'}
                                >
                                    User Management
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Manage all users and their portfolio cards
                                </Typography>
                            </Box>
                        </Stack>
                        <Divider sx={{ 
                            borderColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'rgba(0, 0, 0, 0.1)' 
                        }} />
                    </Box>
                    <Box p={3}>
                        <UsersTable 
                            users={usersWithCards} 
                            loading={loading} 
                            showActions={true}
                            onToggleCardEnabled={handleToggleCardEnabled}
                            onToggleUserActive={handleToggleUserActive}
                        />
                    </Box>
                </GlassCard>
            </motion.div>

            {/* Dialogs and Notifications */}
            <CreateCardDialog 
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCardCreated={handleCardCreated}
            />
            
            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={6000} 
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity="success" 
                    sx={{ 
                        width: '100%',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(67, 160, 71, 0.9) 100%)'
                            : undefined
                    }}
                >
                    New card created successfully!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminDashboard; 